// Date and number formatting utilities
import { formatInTimeZone } from 'date-fns-tz';

export const NY_TIME_ZONE = 'America/New_York';

export const pad = (n: number) => n.toString().padStart(2, '0');

export const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const fmtMDY = (s?: string) => {
  if (!s) return 'mm/dd/yyyy';
  // Handle YYYY-MM-DD manually to avoid timezone issues
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-').map(Number);
    return `${pad(m)}/${pad(d)}/${y}`;
  }
  const d = new Date(s);
  if (isNaN(d.getTime())) return 'mm/dd/yyyy';
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`;
};

export const fmtLongDate = (s: string) => {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return formatInTimeZone(d, NY_TIME_ZONE, 'dd MMM yyyy');
};

export const fmtWeekDay = (s: string) => {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return formatInTimeZone(d, NY_TIME_ZONE, 'EEEE, MMMM d, yyyy');
};

export const formatNyDateTime = (timestampMs: number, pattern = 'MM/dd/yyyy HH:mm') => {
  const d = new Date(timestampMs);
  if (Number.isNaN(d.getTime())) return '';
  return formatInTimeZone(d, NY_TIME_ZONE, pattern);
};

export const getNyDayKey = (timestampMs: number) => formatNyDateTime(timestampMs, 'yyyy-MM-dd');

export const getNyDayStartMs = (date: Date) => {
  const dayKey = formatInTimeZone(date, NY_TIME_ZONE, 'yyyy-MM-dd');
  const [year, month, day] = dayKey.split('-').map(Number);
  const utcMidnight = Date.UTC(year, month - 1, day, 0, 0, 0, 0);
  const inNy = new Date(new Date(utcMidnight).toLocaleString('en-US', { timeZone: NY_TIME_ZONE }));
  const offsetMs = inNy.getTime() - utcMidnight;
  return utcMidnight - offsetMs;
};

export const fmtMoney = (n: number, showSign = false) => {
  const sign = n < 0 ? '-' : (showSign ? '+' : '');
  const v = Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 });
  return `${sign}$${v}`;
};

// Smart money formatter: hide cents when they are zero (e.g. $12 instead of $12.00),
// otherwise show two decimals. Keeps optional sign like fmtMoney.
export const fmtMoneySmart = (n: number, showSign = false) => {
  const sign = n < 0 ? '-' : (showSign ? '+' : '');
  const abs = Math.abs(n);
  const cents = Math.round((abs - Math.floor(abs)) * 100);
  const useDecimals = cents !== 0;
  const v = abs.toLocaleString('en-US', { minimumFractionDigits: useDecimals ? 2 : 0, maximumFractionDigits: useDecimals ? 2 : 0 });
  return `${sign}$${v}`;
};

export const fmtShortDate = (s: string) => {
  // Handle YYYY-MM-DD manually to avoid timezone issues
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [, m, d] = s.split('-').map(Number);
    return `${pad(m)}/${pad(d)}`;
  }
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  return `${month}/${day}`;
};

export const fmtMonthDay = (s: string) => {
  // Handle YYYY-MM-DD manually to avoid timezone issues
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [, m, d] = s.split('-').map(Number);
    return `${pad(m)}/${pad(d)}`;
  }
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  return `${month}/${day}`;
};

export const getYearFromDate = (s: string) => {
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  return d.getFullYear().toString();
};

// Additional utility functions for widgets
export const hash = (s: string) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return h >>> 0;
};

export const fmtMoneyWithDecimals = (n: number, decimals: number = 0) => {
  const sign = n < 0 ? '-' : '';
  const v = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  return `${sign}$${v}`;
};

// Format tooltip date consistently as MM/DD/YYYY. Accepts numeric timestamps or
// date strings (including YYYY-MM-DD). Uses UTC methods for timestamps to avoid
// timezone-related off-by-one issues.
export const fmtTooltipDate = (raw?: string | number | null) => {
  if (raw == null) return '';
  if (typeof raw === 'number') {
    return formatNyDateTime(raw, 'MM/dd/yyyy');
  }
  if (typeof raw === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return fmtMDY(raw);
    const n = Number(raw);
    if (!Number.isNaN(n)) {
      return formatNyDateTime(n, 'MM/dd/yyyy');
    }
    // Fallback: try to parse as a Date string
    const d = new Date(raw);
    if (!isNaN(d.getTime())) return formatNyDateTime(d.getTime(), 'MM/dd/yyyy');
    return raw;
  }
  return String(raw);
};